import Resolver from "@/api/resolver";
import { MIN_DATA_PATIENT, UNIT_ALL } from "@/components/shared/util";
import { LabConfig, LocationType } from "@/store/types";
import _ from "lodash";
import { awicu } from "./awicu";
import { awob } from "./awob";
import { awor } from "./awor";
import { awpacu } from "./awpacu";
import { Patient } from "./types";

export interface ViewConfig {
  humanReadable: string;
  component: string;
  routerString: string;
  acuityComponent?: string;
  itemComponent?: string;
  headerComponent?: string;
  defaultSortRoute?: string;
}

const defaultConfig: any = {
  census: {
    legend: [],
    legendTower: [],
    customUnits: [],
    attending: {
      filterFunction: (patient: Patient) => {
        return !patient.census.roomEmpty;
      },
      sparkline: [
        { key: "BPMAP", title: "BPMAP", max: 150, range: 60 },
        { key: "HeartRate", title: "HR", max: 150, range: 60 },
        { key: "SpO2", title: "SpO₂", min: 80, max: 100, range: 60 },
        {
          key: "EndTidalCO2",
          title: "EtCO₂",
          levelKey: "CO2",
          max: 60,
          range: 60
        }
      ]
    },
    grid: {
      filterFunction: () => true
    },
    gridCardClass: "GridCard",
    LegendDescription: [
      { icon: "alertphys1.png", label: "You need update me in the config!" }
    ]
  },
  body: {
    defaultPhoneLabel: "Default Phone",
    barCharts: {},
    buttons: {},
    bodyValues: {},
    legend: [
      { label: "No Data", paths: ["legendNoData"] },
      {
        label: "Within Normal Limits",
        mobileLabel: "Normal",
        paths: ["legendGreen"]
      },
      { label: "Marginal", paths: ["legendYellow"] },
      { label: "Abnormal", paths: ["legendRed"] },
      { label: "No Risk", paths: ["legendNoRisk"] },
      {
        label: "Organ has Risk Factors / Comorbidities",
        mobileLabel: "Organ at Risk",
        paths: ["legendRisk"]
      }
    ],
    patientLegend: [
      { label: "No Data", paths: ["legendNoData"] },
      {
        label: "Data being received",
        paths: ["legendGreen"]
      }
    ],
    patientDetailBodyComponent: "",
    patientDetialLabels: {
      los: true
    },
    patientDetailBmi: 30,
    bottomPaddingMultiplier: 1,
    configOverrides: {}
  },
  popups: {},
  defaultPopupDescription: {
    graphRanges: [
      { text: "1 hour", hours: 1 },
      { text: "2 hours", hours: 2 },
      { text: "Entire Case", hours: 0 }
    ],
    graphStartingIndex: 0
  },
  parts: {},
  global: {
    showPagingIcon: true,
    showOrderSet: false
  },
  help: {
    paging: {
      fields: [{ key: "description", text: "Alert Description" }],
      // Show or hide the description sections
      description: {
        who: true,
        isOB: false
      }
    },
    tabs: [
      { title: "Instructions", key: "Instructions" },
      {
        title: "Color Limits",
        key: "ColorLimits",
        description:
          "A breakdown of the color levels for the organs, labs and elements."
      },
      {
        title: "Alerts & Icons",
        key: "Alerts",
        description: "List of alerts and associated icons."
      },
      {
        title: "Paging",
        key: "Paging",
        description: "An explanation of the notification logic."
      },
      {
        title: "Standard References",
        key: "StandardReferences",
        description: "Clinical references and site configured links."
      },
      {
        title: "Emergency References",
        key: "EmergencyReferences",
        description: "Institution specific emergency protocols."
      },
      {
        title: "Accessibility",
        key: "Accessibility",
        description: "Support for visual deficiencies."
      },
      {
        title: "Verification & Testing",
        key: "Verification",
        description:
          "Tools for helping assure that the system is functioning properly."
      }
    ]
  },
  icons: {
    body: {
      // Any alert with this slot or above (but less than the tower 90 threshold) is displayed on the body panel
      minSlot: 40
    }
  }
};

defaultConfig.body.configOverrides[MIN_DATA_PATIENT] = {
  heart: {
    atomId: "heartRate"
  },
  lungs: {
    atomId: "lungRate"
  }
};

if (!Resolver.demo) {
  defaultConfig.census.customUnits.push({
    unit: UNIT_ALL,
    label: UNIT_ALL,
    type: 1,
    unitID: "",
    services: [""]
  });
}

defaultConfig.body.legend.forEach((item: any) => {
  item.paths = item.paths.map((path: string) => {
    return `${path}.png`;
  });
});

// TODO: Have some defaults that don't requrie maintaining this list
const parts = [
  "liver",
  "lKidney",
  "uterusGroup",
  "ulcerGroup",
  "rKidney",
  "brain",
  "heart",
  "lungs",
  "airway",
  "spine",
  "ett",
  "icdGroup",
  "temp",
  "rightKidneyText",
  "leftKidneyText",
  "eblText",
  "heartNeedDataText",
  "heartText",
  "heartVolumeText",
  "lungsText",
  "lungRateOnlyText",
  "brainText",
  "chestTube",
  "pump",
  "activity",
  "cough",
  "coughText",
  "mouthAndChin"
];
const popups = [
  "Airway",
  "Allergies",
  "Brain",
  "PatientHandoff",
  "Heart",
  "InfectiousDiseases",
  "Infusions",
  "ioBalance",
  "Creatinine",
  "Urine",
  "Ebl",
  "Liver",
  "Lungs",
  "O2",
  "PACUCheclist",
  "PainScore",
  "Temperature",
  "Ulcer",
  "LegendDescription",
  "inr",
  "review",
  "tower",
  "bypass"
];

const fullscreenPopups = [
  "PatientHandoff",
  "review",
  "tower",
  "bypass",
  "PostOpReview"
];

export const buttons = [
  "sofa",
  "sirs",
  "deteriorationIndex",
  "weight",
  "ioBalance",
  "WeightAndIoBalance",
  "InfectiousDiseases",
  "Infusions",
  "Handoff",
  "Allergies",
  "PreOpPainScore",
  "LowMapAlert",
  "OBNotes",
  "PostOpPainScore"
];
export const bodyValues = ["brain", "lungs", "heart", "kidneys"];

const labConfigs: Array<LabConfig> = [
  {
    title: "INR",
    atom: "inr",
    risk: "INR",
    popupName: "INR"
  },
  {
    title: "Plat",
    atom: "platelet",
    popupName: "Platelet_Count"
  },
  {
    title: "Hct",
    atom: "hct",
    popupName: "Hematocrit"
  },
  {
    title: "K+",
    atom: "pot",
    popupName: "Potassium"
  },
  {
    title: "Glu",
    atom: "glucose",
    risk: "Glucose",
    popupName: "Glucose"
  }
];

defaultConfig.labConfigs = labConfigs;

parts.forEach(part => {
  defaultConfig.parts[part] = {
    visible: true,
    canClick: true,
    textScale: 1,
    atomOverride: ""
  };
});

popups.forEach(popup => {
  defaultConfig.popups[popup] = {};
});

defaultConfig.popups.LegendDescription = {
  component: "LegendDescription",
  popupConfig: {
    data: {
      configLocation: "census.LegendDescription"
    }
  }
};

defaultConfig.popups.INR = {
  component: "GenericRiskAndGraph",
  popupConfig: {
    title: "Blood Clotting Risk Factors",
    segments: [{ primary: "general", secondary: "inr" }],
    graphStartingIndex: 2,
    graphs: [
      {
        title: "INR",
        source: "INR",
        suggestedMax: 2
      },
      {
        title: "PTT",
        source: "PTT",
        suggestedMax: 50
      },
      {
        title: "ACT",
        source: "ACT",
        suggestedMax: 500
      }
    ]
  }
};

defaultConfig.popups.Platelet_Count = {
  component: "GenericRiskAndGraph",
  popupConfig: {
    title: "Platelet Count x 1000",
    graphStartingIndex: 2,
    graphs: [
      {
        source: "PlateletCount",
        suggestedMax: 200
      }
    ]
  }
};

defaultConfig.popups.Hematocrit = {
  component: "Hem",
  componentNames: ["TypeAndScreenAndCross"],
  popupConfig: {
    title: "Hematocrit",
    graphStartingIndex: 2,
    graphs: [
      {
        source: "HCT",
        suggestedMax: 40
      }
    ]
  }
};

defaultConfig.popups.Potassium = {
  component: "GenericRiskAndGraph",
  popupConfig: {
    title: "Potassium",
    graphStartingIndex: 2,
    graphs: [
      {
        source: "Potassium",
        suggestedMax: 6
      }
    ]
  }
};

defaultConfig.popups.Glucose = {
  component: "GenericRiskAndGraph",
  popupConfig: {
    title: "Glucose",
    segments: [{ primary: "general", secondary: "glucose" }],
    graphStartingIndex: 2,
    graphs: [
      {
        source: "Glucose",
        suggestedMax: 140
      }
    ]
  }
};

fullscreenPopups.forEach(name => {
  if (!defaultConfig.popups[name]) {
    defaultConfig.popups[name] = {};
  }
  defaultConfig.popups[name].location = LocationType.fullscreen;
});

defaultConfig.popups.Heart.display = {
  currentStatus: true,
  grouped: false,
  shockIndexGraph: false,
  bloodPressureGraph: true,
  pulsePressureOnGraph: false
};

defaultConfig.popups.Brain.display = {
  showOrRisks: false,
  showIcuRisks: false,
  showMacText: false
};

defaultConfig.popups.ioBalance.display = {
  adjust: true
};

defaultConfig.popups.PainScore.display = {
  graph: false
};

defaultConfig.popups.PatientHandoff.display = {
  or: true,
  pacu: false
};

buttons.forEach(part => {
  defaultConfig.body.buttons[part] = {
    visible: true
  };
});

bodyValues.forEach(value => {
  defaultConfig.body.bodyValues[value] = {
    visible: true
  };
});

defaultConfig.parts.uterusGroup.visible = false;
defaultConfig.parts.spine.visible = false;
defaultConfig.parts.brainText.visible = false;
defaultConfig.parts.lungs.co2Title = "CO<sub>2</sub>";
defaultConfig.parts.lungs.co2Atom = "co2";
defaultConfig.parts.mouthAndChin.canClick = false;

defaultConfig.body.buttons.sofa.visible = false;
defaultConfig.body.buttons.sirs.visible = false;
defaultConfig.body.buttons.deteriorationIndex.visible = false;
defaultConfig.body.buttons.weight.visible = false;
defaultConfig.body.buttons.Handoff.visible = false;
defaultConfig.body.buttons.WeightAndIoBalance.visible = false;
defaultConfig.body.buttons.OBNotes.visible = false;
defaultConfig.body.buttons.PreOpPainScore.visible = false;
defaultConfig.body.buttons.PostOpPainScore.visible = false;

export function cloneConfig() {
  return _.cloneDeep(defaultConfig);
}

export function booleanFilter(input: any) {
  return input ? "Yes" : "";
}

export const wbcLabConfig = {
  title: "WBC",
  atom: "wbc",
  popupName: "White_Blood_Cell_Count"
};

export const White_Blood_Cell_Count = {
  component: "GenericRiskAndGraph",
  popupConfig: {
    title: "White Blood Cell Count x 1000",
    graphs: [
      {
        source: "WBC",
        min: 0,
        suggestedMax: 12
      }
    ]
  }
};

export const configs = Object.freeze({
  awor: awor(),
  awob: awob(),
  awicu: awicu(),
  awpacu: awpacu()
});
